import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { tLang } from "@/contexts/PortalLanguageContext";

const CDN   = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";
const EMBER = "#FF6B35";
const NAVY  = "#1A1A2E";

export default function AboutES() {
  return (
    <>
      <Helmet>
        <title>{tLang("es", "about.meta.title", "Sobre Fyrris")}</title>
        <meta name="description" content={tLang("es", "about.meta.description")} />
        <meta name="keywords" content="Fyrris sobre nosotros, marketplace HPB, AR sector chimeneas, BBQ exteriores, tecnología AR venta minorista" />
        <meta property="og:title" content={tLang("es", "about.meta.ogTitle")} />
        <meta property="og:description" content={tLang("es", "about.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang("es", "about.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang("es", "about.meta.ogDescription")} />
      </Helmet>

      {/* Hero */}
      <section style={{ position: "relative", height: "65vh", overflow: "hidden" }}>
        <img
          src={`${CDN}/hero/About%20Page%20Hero.png`}
          alt="Fyrris — conectando propietarios y minoristas HPB a través de la RA"
          loading="eager"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center center",
            zIndex: 0,
          }}
        />
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(to bottom, rgba(26,26,46,0.85) 0%, rgba(26,26,46,0.75) 100%)",
        }} />
        <div style={{
          position: "relative", zIndex: 10,
          height: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          textAlign: "center",
          padding: "0 1.5rem",
        }}>
          <h1 style={{
            fontSize: "clamp(2.25rem, 5vw, 4rem)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.15,
            margin: 0,
          }}>
            Sobre Fyrris
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: "1.125rem",
            maxWidth: "36rem",
            marginTop: "1rem",
            lineHeight: "1.75",
          }}>
            {tLang("es", "about.mission.body", "Fyrris conecta a los propietarios con los productos que aman y los minoristas que los venden — a través de la realidad aumentada.")}
          </p>
        </div>
      </section>

      <div style={{ position: "relative", zIndex: 20, backgroundColor: NAVY }}>

        {/* Qué hacemos */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8">Qué hacemos</h2>
            <ul className="space-y-6 text-gray-300 max-w-3xl">
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Visualización en realidad aumentada</strong>
                <span className="leading-relaxed">Ve los productos tal como se verían en tu hogar — a escala real, antes de visitar una sala de exposición.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Buscador inteligente de tiendas</strong>
                <span className="leading-relaxed">Descubre salas de exposición e instaladores cercanos al instante. Cada sesión de RA muestra la tienda más próxima que tiene ese producto.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Portal para minoristas</strong>
                <span className="leading-relaxed">Gestiona listados, analíticas y la relación con clientes desde un único panel de control.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">IA y automatización 3D</strong>
                <span className="leading-relaxed">Acelera la incorporación de productos y la calidad de visualización a escala — sin necesidad de personal técnico.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Nuestra historia */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Nuestra historia</h2>
            <div className="space-y-5 text-lg text-gray-300 leading-relaxed max-w-3xl">
              <p>
                Fyrris fue creada por un equipo apasionado por la innovación, el diseño y la conexión humana.
                Vimos un sector lleno de oficio y emoción, pero sin las herramientas digitales
                que merecía.
              </p>
              <p>
                Hoy, Fyrris cierra esa brecha, ayudando a minoristas y fabricantes a prosperar
                en un futuro impulsado por la RA, mientras los clientes diseñan sus momentos
                de reunión ideales.
              </p>
            </div>
          </div>
        </section>

        {/* Nuestra visión */}
        <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", marginBottom: "4rem" }} />
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24" style={{ marginTop: "4rem" }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Nuestra visión</h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">
              Convertirnos en el marketplace de RA de referencia para chimeneas, BBQ y vida al
              aire libre — inspirando a las personas a diseñar sus momentos y redescubrir la alegría
              de estar juntos en sus espacios exteriores.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 sm:px-6 lg:px-8 pb-24 md:pb-32 border-t border-gray-800 pt-16">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 text-center" style={{ padding: "3rem 2rem" }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
                ¿Eres minorista?
              </h2>
              <p className="text-gray-300 max-w-xl mx-auto leading-relaxed text-lg">
                Únete a la red Fyrris y conecta con compradores que ya buscan
                productos como los tuyos — guiados directamente a tu tienda.
              </p>
              <div style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <Link
                  to="/es/minoristas/precios"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
                  style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
                >
                  Ver planes para minoristas →
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
